import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Group, GroupKind, GroupKindDefinition, Person, PersonPage } from '@up2d8/contracts';
import { useSession } from '../../shared/session';
import { useProblemText } from '../../shared/use-problem-text';
import {
  AdminIcon,
  Badge,
  Btn,
  Chip,
  Field,
  IconBtn,
  Input,
  Modal,
  Note,
  Select,
  Stack,
  Table,
  type BadgeTone,
} from '../admin-kit';

/**
 * Groepssoorten in de woorden van het bedrijf (FBK-5, ADR-0092), en wat de groepenpagina erbij
 * nodig heeft: de naam van een soort, de boom van groepen, en een kiezer voor leden.
 */

export const BASE_KINDS: GroupKind[] = ['department', 'line', 'shift', 'site', 'other'];

/** De kleur per basis (O1): Afdeling navy, Lijn blauw, Ploeg groen, Vestiging oranje. */
export const KIND_TONE: Record<GroupKind, BadgeTone> = {
  department: 'navy',
  line: 'info',
  shift: 'ok',
  site: 'attention',
  other: 'neutral',
};

export function useGroupKinds() {
  const { api } = useSession();
  return useQuery({
    queryKey: ['directory', 'group-kinds'],
    queryFn: async (): Promise<GroupKindDefinition[]> => {
      const { data, error } = await api.GET('/api/v1/group-kinds');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: GroupKindDefinition[] }).items;
    },
  });
}

/** De naam van een soort: de eigen naam, of de vertaalde naam van de basis. */
export function kindLabel(t: TFunction, kind: { name: string | null; baseKind: GroupKind }) {
  return kind.name ?? t(`groups.kinds.${kind.baseKind}`);
}

/** De soort van een groep: haar eigen soort, of de vaste soort van haar basis. */
export function kindOfGroup(
  group: Pick<Group, 'kind' | 'kindId'>,
  kinds: readonly GroupKindDefinition[],
): GroupKindDefinition | undefined {
  return group.kindId
    ? kinds.find((kind) => kind.id === group.kindId)
    : kinds.find((kind) => kind.isSystem && kind.baseKind === group.kind);
}

/** De badge van de soort van een groep, met de eigen kleur als die er is. */
export function KindBadge({
  group,
  kinds,
}: {
  group: Pick<Group, 'kind' | 'kindId' | 'kindName'>;
  kinds: readonly GroupKindDefinition[];
}) {
  const { t } = useTranslation();
  const kind = kindOfGroup(group, kinds);
  const label = group.kindName ?? (kind ? kindLabel(t, kind) : t(`groups.kinds.${group.kind}`));
  if (kind?.color) {
    return (
      <span
        className="a-badge"
        style={{
          background: `color-mix(in srgb, ${kind.color} 14%, white)`,
          color: `color-mix(in srgb, ${kind.color} 75%, black)`,
        }}
      >
        {label}
      </span>
    );
  }
  return <Badge tone={KIND_TONE[group.kind]}>{label}</Badge>;
}

/**
 * De groepen als boom: elke groep met haar diepte, ouders vóór kinderen. Een groep waarvan de
 * ouder niet in de lijst staat (gefilterd of verwijderd), staat bovenaan.
 */
export function asTree(groups: readonly Group[]): { group: Group; depth: number }[] {
  const ids = new Set(groups.map((group) => group.id));
  const children = new Map<string | null, Group[]>();
  for (const group of groups) {
    const parent = group.parentGroupId && ids.has(group.parentGroupId) ? group.parentGroupId : null;
    children.set(parent, [...(children.get(parent) ?? []), group]);
  }
  const out: { group: Group; depth: number }[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    for (const group of children.get(parent) ?? []) {
      if (seen.has(group.id)) continue;
      seen.add(group.id);
      out.push({ group, depth });
      walk(group.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** Een groep en alles eronder: die kunnen niet de ouder van deze groep worden. */
export function selfAndBelow(groups: readonly Group[], groupId: string): Set<string> {
  const below = new Set([groupId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const group of groups) {
      if (group.parentGroupId && below.has(group.parentGroupId) && !below.has(group.id)) {
        below.add(group.id);
        grew = true;
      }
    }
  }
  return below;
}

/** Leden kiezen door te zoeken: bij duizenden medewerkers is een keuzelijst onwerkbaar. */
export function PeoplePicker({
  value,
  onChange,
}: {
  value: { id: string; name: string }[];
  onChange: (next: { id: string; name: string }[]) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const [search, setSearch] = useState('');
  const term = search.trim();

  const found = useQuery({
    queryKey: ['directory', 'people-picker', term],
    enabled: term.length >= 2,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/people', {
        params: { query: { search: term, status: 'active', limit: 8 } } as never,
      });
      return ((data as PersonPage | undefined)?.items ?? []) as Person[];
    },
  });

  const chosen = new Set(value.map((person) => person.id));
  return (
    <Stack>
      <Field label={t('groupTree.members')} hint={t('groupTree.membersHint')}>
        {(props) => (
          <Input
            {...props}
            data-testid="group-member-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        )}
      </Field>
      {term.length >= 2 && (found.data ?? []).length > 0 ? (
        <div className="a-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {(found.data ?? [])
            .filter((person) => !chosen.has(person.id))
            .map((person) => (
              <Btn
                key={person.id}
                variant="sec"
                size="sm"
                icon={<AdminIcon name="plus" />}
                testId="group-member-suggestion"
                onClick={() =>
                  onChange([
                    ...value,
                    { id: person.id, name: `${person.firstName} ${person.lastName}` },
                  ])
                }
              >
                {person.firstName} {person.lastName}
              </Btn>
            ))}
        </div>
      ) : null}
      {value.length > 0 ? (
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
          data-testid="group-members-chosen"
        >
          {value.map((person) => (
            <Chip
              key={person.id}
              onRemove={() => onChange(value.filter((other) => other.id !== person.id))}
            >
              {person.name}
            </Chip>
          ))}
        </div>
      ) : null}
    </Stack>
  );
}

/** Soorten beheren: hernoemen, kleur, uit- en aanzetten, en een eigen soort toevoegen. */
export function GroupKindsModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const text = useProblemText();
  const queryClient = useQueryClient();
  const kinds = useGroupKinds();
  const [name, setName] = useState('');
  const [base, setBase] = useState<GroupKind>('other');

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['directory'] });

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/group-kinds', {
        body: { name: name.trim(), baseKind: base } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setName('');
      await refresh();
    },
  });

  const update = useMutation({
    mutationFn: async (input: { id: string; changes: Record<string, unknown> }) => {
      const { error } = await api.PATCH('/api/v1/group-kinds/{kindId}', {
        params: { path: { kindId: input.id } },
        body: input.changes as never,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const error = create.error ?? update.error;

  return (
    <Modal
      open
      wide
      onClose={onClose}
      testId="group-kinds"
      title={t('groupTree.kindsTitle')}
      sub={t('groupTree.kindsSub')}
      footer={
        <Btn variant="sec" onClick={onClose}>
          {t('common.close')}
        </Btn>
      }
    >
      <Stack>
        {error ? (
          <Note tone="danger" role="alert">
            {text(error, t('errors.generic'))}
          </Note>
        ) : null}
        <Table label={t('groupTree.kindsTitle')}>
          <thead>
            <tr>
              <th>{t('groups.name')}</th>
              <th>{t('groupTree.worksAs')}</th>
              <th>{t('groupTree.color')}</th>
              <th className="num">{t('groupTree.groupCount')}</th>
              <th className="act" aria-label={t('common.actions')} />
            </tr>
          </thead>
          <tbody>
            {(kinds.data ?? []).map((kind) => (
              <KindRow
                key={kind.id}
                kind={kind}
                busy={update.isPending}
                onSave={(changes) => update.mutate({ id: kind.id, changes })}
              />
            ))}
          </tbody>
        </Table>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label={t('groupTree.newKind')}>
            {(props) => (
              <Input
                {...props}
                data-testid="group-kind-new-name"
                placeholder={t('groupTree.newKindPlaceholder')}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>
          <Field label={t('groupTree.worksAs')}>
            {(props) => (
              <Select
                {...props}
                value={base}
                onChange={(event) => setBase(event.target.value as GroupKind)}
              >
                {BASE_KINDS.map((option) => (
                  <option key={option} value={option}>
                    {t(`groups.kinds.${option}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Btn
            icon={<AdminIcon name="plus" />}
            testId="group-kind-add"
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {t('groupTree.addKind')}
          </Btn>
        </div>
        <Note>{t('groupTree.kindsNote')}</Note>
      </Stack>
    </Modal>
  );
}

function KindRow({
  kind,
  busy,
  onSave,
}: {
  kind: GroupKindDefinition;
  busy: boolean;
  onSave: (changes: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(kind.name ?? '');
  const standard = t(`groups.kinds.${kind.baseKind}`);
  const changed = name.trim() !== (kind.name ?? '');

  return (
    <tr style={kind.archived ? { opacity: 0.55 } : undefined} data-testid="group-kind-row">
      <td>
        <Input
          aria-label={t('groups.name')}
          value={name}
          placeholder={kind.isSystem ? standard : undefined}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => {
            if (!changed) return;
            // Een vaste soort leeg maken is terug naar de standaardnaam.
            if (!name.trim() && !kind.isSystem) return setName(kind.name ?? '');
            onSave({ name: name.trim() || null });
          }}
        />
      </td>
      <td className="a-muted">{standard}</td>
      <td>
        <input
          type="color"
          aria-label={t('groupTree.color')}
          value={kind.color ?? '#64748b'}
          onChange={(event) => onSave({ color: event.target.value })}
        />
      </td>
      <td className="num">{kind.groupCount}</td>
      <td className="act">
        <IconBtn
          label={kind.archived ? t('groupTree.turnOn') : t('groupTree.turnOff')}
          icon={<AdminIcon name={kind.archived ? 'eye' : 'eyeOff'} />}
          onClick={() => {
            if (!busy) onSave({ archived: !kind.archived });
          }}
        />
      </td>
    </tr>
  );
}
